import React, { useContext, useEffect, useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  LinearProgress,
  useTheme,
  Button,
} from '@mui/material';
import { tokens } from '../theme';
import { FilterContext } from '../contexts/FilterContext';
import { dateToSqlDate } from '../helpers/dateFuncs';
import fetchData from '../helpers/fetchData';
import CustomerRadioFilter from './CustomerRadioFilter';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import DownloadIcon from '@mui/icons-material/Download';
import * as XLSX from 'xlsx';
import { useSourceDisplayConfig, getDisplayName } from '../hooks/useSourceDisplayConfig';

interface SourcePerformance {
  source: string;
  total: number;
  new: number;
  duplicate: number;
  error: number;
  qualityScore: number;
  trend: number;
  trendPercent: number;
}

interface TopSourcesPerformanceTableProps {
  selectedCustomers: string[];
}

type SortOrder = 'asc' | 'desc';
type SortColumn = 'source' | 'total' | 'qualityScore' | 'trend';

const TopSourcesPerformanceTable: React.FC<TopSourcesPerformanceTableProps> = ({
  selectedCustomers,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { filterState } = useContext(FilterContext)!;

  const [selectedCustomer, setSelectedCustomer] = useState<string>('ft');
  const { configMap } = useSourceDisplayConfig(selectedCustomer);
  const [sourcePerformance, setSourcePerformance] = useState<SourcePerformance[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortColumn>('total');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  useEffect(() => {
    const fetchPerformanceData = async () => {
      if (!filterState.startDate || !filterState.endDate) {
        setSourcePerformance([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const options = {
          start_date: dateToSqlDate(filterState.startDate),
          end_date: dateToSqlDate(filterState.endDate),
        };

        const data = await fetchData<SourcePerformance[]>(
          `/api/daily-controlling/source-performance/${selectedCustomer}`,
          options,
        );

        const flatData = Array.isArray(data) ? data.flat() : [];
        setSourcePerformance(flatData);
      } catch (err: any) {
        console.error('Error fetching source performance data:', err);
        setError(err.message || 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceData();
  }, [filterState.startDate, filterState.endDate, selectedCustomer]);

  const sortedData = useMemo(() => {
    const sorted = [...sourcePerformance];
    sorted.sort((a, b) => {
      let aValue: number | string = a[sortColumn];
      let bValue: number | string = b[sortColumn];

      if (sortColumn === 'source') {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
        return sortOrder === 'asc' ? (aValue < bValue ? -1 : 1) : aValue > bValue ? -1 : 1;
      }

      return sortOrder === 'asc'
        ? Number(aValue) - Number(bValue)
        : Number(bValue) - Number(aValue);
    });
    return sorted;
  }, [sourcePerformance, sortColumn, sortOrder]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
  };

  const handleExport = () => {
    const exportData = sortedData.map((source) => ({
      Source: getDisplayName(configMap, source.source),
      'Total Leads': source.total,
      'New Leads': source.new,
      'Duplicate Leads': source.duplicate,
      'Error Leads': source.error,
      'Quality Score (%)': source.qualityScore.toFixed(1),
      Trend: source.trend,
      'Trend (%)': source.trendPercent.toFixed(1),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Top Sources');
    XLSX.writeFile(workbook, `top_sources_${selectedCustomer}.xlsx`);
  };

  const getTrendIcon = (trend: number) => {
    if (trend > 0)
      return <TrendingUpIcon fontSize="small" sx={{ color: colors.greenAccent[500] }} />;
    if (trend < 0)
      return <TrendingDownIcon fontSize="small" sx={{ color: colors.redAccent[500] }} />;
    return <TrendingFlatIcon fontSize="small" sx={{ color: colors.grey[500] }} />;
  };

  const maxTotal = Math.max(...sourcePerformance.map((s) => s.total), 1);

  if (loading) {
    return (
      <Box p={2}>
        <Typography>Laden...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2} color="error.main">
        Error: {error}
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight="600">
          Top Sources nach Performance
        </Typography>
        <Box display="flex" gap={2} alignItems="center">
          <CustomerRadioFilter
            selectedCustomer={selectedCustomer}
            onSelectionChange={setSelectedCustomer}
          />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleExport}
            size="small"
            disabled={sourcePerformance.length === 0}
          >
            Excel Export
          </Button>
        </Box>
      </Box>

      {sourcePerformance.length === 0 ? (
        <Box p={2}>
          <Typography color="text.secondary">
            Keine Daten für den ausgewählten Zeitraum verfügbar.
          </Typography>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ backgroundColor: colors.primary[400] }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'source'}
                    direction={sortColumn === 'source' ? sortOrder : 'asc'}
                    onClick={() => handleSort('source')}
                  >
                    Source
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortColumn === 'total'}
                    direction={sortColumn === 'total' ? sortOrder : 'asc'}
                    onClick={() => handleSort('total')}
                  >
                    Total Leads
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center" sx={{ minWidth: 150 }}>
                  Volume
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={sortColumn === 'qualityScore'}
                    direction={sortColumn === 'qualityScore' ? sortOrder : 'asc'}
                    onClick={() => handleSort('qualityScore')}
                  >
                    Quality Score
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">New</TableCell>
                <TableCell align="right">Duplicate</TableCell>
                <TableCell align="right">Error</TableCell>
                <TableCell align="center">
                  <TableSortLabel
                    active={sortColumn === 'trend'}
                    direction={sortColumn === 'trend' ? sortOrder : 'asc'}
                    onClick={() => handleSort('trend')}
                  >
                    Trend
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedData.map((source) => (
                <TableRow key={source.source} hover>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {getDisplayName(configMap, source.source)}
                  </TableCell>
                  <TableCell align="right">{source.total.toLocaleString('de-DE')}</TableCell>
                  <TableCell>
                    <LinearProgress
                      variant="determinate"
                      value={(source.total / maxTotal) * 100}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        backgroundColor: colors.primary[700],
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: colors.blueAccent[500],
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {source.qualityScore.toFixed(1)}%
                  </TableCell>
                  <TableCell align="right" sx={{ color: colors.greenAccent[500] }}>
                    {source.new.toLocaleString('de-DE')}
                  </TableCell>
                  <TableCell align="right" sx={{ color: colors.redAccent[400] }}>
                    {source.duplicate.toLocaleString('de-DE')}
                  </TableCell>
                  <TableCell align="right" sx={{ color: colors.redAccent[600] }}>
                    {source.error.toLocaleString('de-DE')}
                  </TableCell>
                  <TableCell align="center">
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      {getTrendIcon(source.trend)}
                      <Typography variant="body2">
                        {source.trendPercent > 0 ? '+' : ''}
                        {source.trendPercent.toFixed(0)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TopSourcesPerformanceTable;
